import { Checkbox, FormControlLabel, Stack, TextField } from "@mui/material";
import { useForm } from "../../../../hooks/useForm/useForm";
import { Database } from "../../../../../../lib/supabase";
import { FormProps } from "../../../form-types";
import { SupabaseTableEnum } from "../../../../../../lib/supabase/supabaseTypes";
import { JobStatusSelect } from "../../../form-input-components/selects/job-status.select";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { CustomDatePicker } from "@/dentlab/src/components/custom-date-picker/custom-date-picker.component";
import { useEffect, useState } from "react";
import { TpTier, TpValue } from "@/dentlab/src/types/enums";
import { ProdCountryChoiceSelect } from "../../../form-input-components/selects/prod-country-choice.select";
import { BankAccountSelect } from "../../bank-account/input-components/bank-account.select";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { CustomTextFieldWithPercentage } from "../../../form-input-components/custom-input-components/custom-text-field-with-percentage.component";
import { CustomToggleButtonGroup } from "@/dentlab/src/components/custom-toggle-button/custom-toggle-button-group.component";
import { useAutoSave } from "@/dentlab/src/hooks/useAutosave/useAutosave";

export const JobGeneralForm: React.FC<FormProps> = ({ submitRef = null }) => {
	const supabaseTable = SupabaseTableEnum.JOBS;
	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));

	const { updateJobItems } = useJobStore((state) => ({
		updateJobItems: state.updateJobItems,
	}));

	const { job, changeJobStatus, fetchJob, setPendingChanges } = useJobStore(
		(state) => ({
			job: state.job,
			changeJobStatus: state.changeJobStatus,
			fetchJob: state.fetchJob,
			setPendingChanges: state.setPendingChanges,
		})
	);

	const {
		handleInputChange,
		handleUpdate,
		formData,
		isPendingChanges,

		handleValueChange,
		formErrors,
	} = useForm<Database["public"]["Tables"]["jobs"]["Update"]>(
		{
			organization_id: organization?.id,
		},
		supabaseTable,
		true,
		job?.id,
		"Auftrag"
	);

	const assertSubmit = () => {
		return true;
	};

	const [previousTpValue, setPreviousTpValue] = useState<number | null>(null);

	useAutoSave({
		data: formData,
		onSave: () => onSubmit(new Event("submit") as any),
		shouldSave: isPendingChanges,
	});

	useEffect(() => {
		setPendingChanges(isPendingChanges);
	}, [isPendingChanges]);

	useEffect(() => {
		if (!previousTpValue) {
			setPreviousTpValue(formData.tp_value as number);
		}
	}, [formData]);

	const onSubmit = async (event: React.FormEvent) => {
		const jobId = job?.id;
		if (!jobId) {
			showNotification({
				type: "error",
				message: "Auftrag konnte nicht gefunden werden",
			});
			return;
		}
		delete formData.search_vector;
		if (assertSubmit()) {
			const { success } = await handleUpdate(event, jobId, {
				hideNotification: true,
			});
			if (success) {
				const shouldUpdateItems =
					(formData.tp_value !== previousTpValue &&
						formData.tp_value !== undefined)

				if (shouldUpdateItems) {
					await updateJobItems(
						job.id as number,
						formData.tp_value as number,
					);
					setPreviousTpValue(formData.tp_value as number);
				}
				await fetchJob(job.id as number);
			} else {
				showNotification({
					message:
						"Fehler beim Speichern des Auftrags. Bitte versuchen Sie es erneut.",
					type: "error",
				});
			}
		}
	};

	return (
		<form onSubmit={onSubmit}>
			<Stack direction="column" spacing={2}>
				<div className="my-6 w-full flex flex-row gap-2 items-center">
					<div>Status</div>
					<JobStatusSelect
						value={formData.status?.toString()}
						onChange={(value) => {
							if (job?.id && job?.status)
								changeJobStatus(
									job.id,
									job.status,
									parseInt(value)
								);
						}}
					/>
				</div>
				<div className="my-6 w-full flex flex-row gap-2 items-center">
					<div>Eingangsdatum</div>
					<CustomDatePicker
						initialValue={formData.incoming_order_date ?? ""}
						onChange={(value) =>
							handleValueChange("incoming_order_date", value)
						}
					/>
				</div>

				<div className="my-6 w-full flex flex-row gap-2 items-center">
					<div>Ausgangsdatum</div>
					<CustomDatePicker
						initialValue={formData.outgoing_order_date ?? ""}
						onChange={(value) =>
							handleValueChange("outgoing_order_date", value)
						}
					/>
				</div>
				<div className="border-b border-gray-300 my-6"></div>
				<div>
					<div>TP-Stufe</div>
					<CustomToggleButtonGroup
						value={formData.tp_tier}
						onChange={(value) => {
							handleValueChange("tp_tier", value);
						}}
						options={[
							{ value: TpTier.SV, label: "SV" },
							{ value: TpTier.PP1, label: "PP1" },
							{ value: TpTier.PP2, label: "PP2" },
							{ value: TpTier.PP3, label: "PP3" },
						]}
					/>
				</div>
				<CustomTextFieldWithPercentage
					label="TP-Variation (+/-)"
					name="tp_variation"
					value={formData.tp_variation ?? ""}
					onChange={handleInputChange}
					size="small"
					sx={{
						width: "50%",
					}}
					validationError={
						formData.tp_variation
							? formErrors.tp_variation
							: "Bitte geben Sie eine Zahl ein."
					}
				/>
				<div>
					<div>TP-Wert</div>
					<CustomToggleButtonGroup
						value={formData.tp_value}
						onChange={(value) => {
							if (!value) return;
							handleValueChange("tp_value", value);
						}}
						options={[
							{ value: TpValue.OLD, label: "5.55" },
							{ value: TpValue.NEW, label: "1.0" },
						]}
						isInput
					/>
				</div>
				<FormControlLabel
					control={
						<Checkbox
							checked={
								formData.is_ignore_monthly_discount || false
							}
							onChange={(e) =>
								handleValueChange(
									"is_ignore_monthly_discount",
									e.target.checked
								)
							}
						/>
					}
					label="Monatsrabatt unterdrücken"
				/>
				<div className="border-b border-gray-300 my-6"></div>
				<div>
					<TextField
						label="Zahnfarbe"
						name="tooth_color"
						value={formData.tooth_color || ""}
						onChange={handleInputChange}
						variant="outlined"
						size="small"
						fullWidth
					/>
					<TextField
						label="Beschreibung"
						name="description"
						value={formData.description || ""}
						onChange={handleInputChange}
						variant="outlined"
						size="small"
						fullWidth
						rows={2}
						multiline
					/>
				</div>
				<div className="border-b border-gray-300 my-6"></div>
				<div className="my-6 w-full flex flex-col gap-0">
					<ProdCountryChoiceSelect
						value={formData.prod_country_choice || ""}
						onChange={(value) =>
							handleValueChange("prod_country_choice", value)
						}
					/>
					{formData.prod_country_choice === "other" && (
						<TextField
							label="Fertigungsland (Ausland)"
							name="prod_country_other"
							value={formData.prod_country_other || ""}
							onChange={handleInputChange}
							variant="outlined"
							size="small"
						/>
					)}
					<TextField
						label="MwSt."
						name="tax_rate"
						value={formData.tax_rate || ""}
						onChange={handleInputChange}
						size="small"
					/>
					<BankAccountSelect
						onChange={(value) =>
							handleValueChange("bank_account_id", value)
						}
						value={
							formData.bank_account_id
								? formData.bank_account_id.toString()
								: ""
						}
						helperText="Wenn kein Konto gewählt ist, wird das Standardkonto des Auftragsgebers benutzt."
					/>
				</div>
			</Stack>
		</form>
	);
};
